import FilterSearch from './FilterSearch';
import styles from './FiltersBar.module.scss';
import Timeslicer from 'components/timeslicer/Timeslicer';
import {
    BASE_ROUTE,
    MARKET_ROUTE,
    RM_ROUTE,
    SCENARIO_FOUR_ROUTE,
    SCENARIO_ONE_ROUTE,
    TEAM_ROUTE
} from 'constants/routes';
import { emitMainFilterReset } from 'events/resetAllFilters';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    selectDataIndex,
    selectFiltersIsFetched,
    selectMarketDropdownValues,
    selectMarketLabel,
    selectMarketValue,
    selectRmDropdownValues,
    selectRmLabel,
    selectRmValue,
    selectSeriesIndex,
    selectTeamDropdownValues,
    selectTeamLabel,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerMonthList,
    selectTimeslicerStartMonth,
    selectZonesFetched,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchFiltersInitialValues,
    fetchLatestDataAvailableMonth,
    fetchMainStats,
    fetchZones,
    resetFilters,
    updateTimeslicerStartEndValues
} from 'slices/FiltersSlice';
import { switchOutlierChartPage } from 'slices/ScenarioThreeSlice';
import { resetAll } from 'slices/ScenarioFourSlice';
import { FilterDisplay, FilterType } from 'types/types';
import {
    generateRequestParamsForFilterApi,
    generateRequestParamsForMainStatsApi
} from 'utils/api';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import { increaseEndDateByOneMonth } from 'utils/helpers';
import ClearFilters from './ClearFilters';
import { resetScenarioOneCharts } from 'slices/ScenarioOneSlice';

const FiltersBar = forwardRef((props, ref) => {
    /**********************
     * Props, Local State *
     * *******************/
    const { onTimeSlicerChange, onFiltersChanged } = props;

    const [timeSlicerResetState, toggleTimeSlicerResetState] = useState();

    const [showMarketOptions, setShowMarketOptions] = useState(false);
    const [showTeamOptions, setShowTeamOptions] = useState(false);
    const [showRmOptions, setShowRmOptions] = useState(false);

    const [isMarketInFocus, setIsMarketInFocus] = useState(false);
    const [isTeamInFocus, setIsTeamInFocus] = useState(false);
    const [isRmInFocus, setIsRmInFocus] = useState(false);

    const [marketSearchFieldLength, setMarketSearchFieldLength] = useState(0);
    const [teamSearchFieldLength, setTeamSearchFieldLength] = useState(0);
    const [rmSearchFieldLength, setRmSearchFieldLength] = useState(0);

    const [marketSearchFieldValue, setMarketSearchFieldValue] = useState('');
    const [teamSearchFieldValue, setTeamSearchFieldValue] = useState('');
    const [rmSearchFieldValue, setRmSearchFieldValue] = useState('');

    const dispatch = useDispatch();
    const { pathname: route } = useLocation();
    /***********************
     * Selectors and Memos *
     * ********************/
    const filtersIsFetched = useSelector(selectFiltersIsFetched);
    const zonesFetched = useSelector(selectZonesFetched);
    const tsMonthList = useSelector(selectTimeslicerMonthList);
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);

    const marketLabel = useSelector(selectMarketLabel);
    const teamLabel = useSelector(selectTeamLabel);
    const rmLabel = useSelector(selectRmLabel);

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const marketDropdownValues = useSelector(selectMarketDropdownValues);
    const teamDropdownValues = useSelector(selectTeamDropdownValues);
    const rmDropdownValues = useSelector(selectRmDropdownValues);

    const dataIndex = useSelector(selectDataIndex);
    const seriesIndex = useSelector(selectSeriesIndex);

    /**************************************
     * Get the latest month range for     *
     * time slicer before initializing it *
     * ***********************************/
    useEffect(() => {
        if (!tsMonthList) dispatch(fetchLatestDataAvailableMonth());
    }, []);

    /********************************
     * Initialise Time slicer values *
     * before anything else          *
     * ******************************/
    const initialiseTimeSlicer = () => {
        if (tsMonthList) {
            dispatch(
                updateTimeslicerStartEndValues({
                    start: tsStart || tsMonthList[0],
                    end:
                        tsEnd ||
                        increaseEndDateByOneMonth(
                            tsMonthList[tsMonthList.length - 1]
                        )
                })
            );
        }
    };

    useEffect(() => {
        initialiseTimeSlicer();
    }, [tsMonthList]);

    /************************************
     * Fetch zones before fetching      *
     * initial filter values            *
     * *********************************/
    useEffect(() => {
        if (route === BASE_ROUTE && tsMonthList) {
            dispatch(
                fetchFiltersInitialValues(
                    generateRequestParamsForFilterApi(
                        tsMonthList[0].epoch,
                        increaseEndDateByOneMonth(
                            tsMonthList[tsMonthList.length - 1]
                        ).epoch,
                        zoneValue
                    ).requestParams
                )
            );
        }
    }, [route, tsMonthList]);

    /************************************
     * Fetch initial filter values      *
     * after fetching timeslicer values *
     * *********************************/
    useEffect(() => {
        if (!zonesFetched && !filtersIsFetched && tsMonthList) {
            dispatch(fetchZones());
            dispatch(
                fetchFiltersInitialValues(
                    generateRequestParamsForFilterApi(
                        tsMonthList[0].epoch,
                        increaseEndDateByOneMonth(
                            tsMonthList[tsMonthList.length - 1]
                        ).epoch,
                        zoneValue
                    ).requestParams
                )
            );
        }
    }, [tsMonthList, zonesFetched, tsMonthList]);

    /************************************
     * Fetch main stats values anytime  *
     * market, team, rm or timeslicer   *
     * filter values change             *
     * *********************************/
    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                dispatch(
                    fetchMainStats(
                        generateRequestParamsForMainStatsApi({
                            startMonth: tsStart.epoch,
                            endMonth: tsEnd.epoch,
                            market: marketValue,
                            team: teamValue,
                            rm: rmValue
                        }).requestParams
                    )
                );
            }
        }
    }, [zoneValue, marketValue, teamValue, rmValue, tsStart, tsEnd]);

    const isMarketRoute = route === MARKET_ROUTE;
    const isRmRoute = route === RM_ROUTE;
    const isTeamRoute = route === TEAM_ROUTE;
    const filteredMarketDropdownValues =
        marketDropdownValues &&
        marketDropdownValues.filter((value) => value.label !== 'All');
    const filteredTeamDropdownValues =
        teamDropdownValues &&
        teamDropdownValues.filter((value) => value.label !== 'All');
    const filteredRmDropdownValues =
        rmDropdownValues &&
        rmDropdownValues.filter((value) => value.label !== 'All');

    /**
     * Reset timeslicer
     */
    const resetTimeSlicer = () => {
        initialiseTimeSlicer();
        toggleTimeSlicerResetState(!timeSlicerResetState);
    };

    /**
     * This will reset all filters
     */
    const resetAllFilters = (filterType, isResettingMarketAll) => {
        if (isMarketRoute || isRmRoute || isTeamRoute) {
            if (!isResettingMarketAll) {
                resetTimeSlicer();
            }
            dispatch(switchOutlierChartPage(1));
        } else {
            // reset Scenario One Charts
            if (
                route === SCENARIO_ONE_ROUTE &&
                typeof seriesIndex === 'number' &&
                typeof dataIndex === 'number'
            ) {
                const eChartsInstance = ref.current.getEchartsInstance();
                eChartsInstance.dispatchAction({
                    type: 'unselect',
                    seriesIndex,
                    dataIndex
                });
            }

            if (route === SCENARIO_FOUR_ROUTE) {
                dispatch(resetAll());
            }

            if (!isResettingMarketAll) {
                resetTimeSlicer();
            }
            dispatch(switchOutlierChartPage(1));

            if (tsMonthList) {
                dispatch(
                    fetchFiltersInitialValues(
                        generateRequestParamsForFilterApi(
                            tsMonthList[0].epoch,
                            increaseEndDateByOneMonth(
                                tsMonthList[tsMonthList.length - 1]
                            ).epoch,
                            zoneValue
                        ).requestParams
                    )
                );
            }

            dispatch(resetFilters());
            dispatch(resetScenarioOneCharts());
        }
        emitMainFilterReset({ reset: true });
    };

    /************************************
     * Reset all filters every time     *
     * Landing page component mounts    *
     * (when user navigates to landing) *
     * *********************************/
    useEffect(() => {
        if (route === BASE_ROUTE) {
            resetAllFilters();
        }
    }, [route]);

    const renderFilterStaticText = (label, value) => {
        return (
            <div className={styles.staticTextContainer}>
                <span className={styles.textLeft}>{label}</span>
                <span className={styles.textRight}>{value}</span>
            </div>
        );
    };

    return (
        <div className={styles.filterRowContainer}>
            <div className={styles.leftFiltersWrapper}>
                {!isRmRoute && !isTeamRoute ? (
                    <FilterSearch
                        style={isMarketRoute ? { marginRight: '30px' } : {}}
                        filterName={FilterDisplay.MARKET}
                        filterType={FilterType.MARKET}
                        searchFieldValue={marketSearchFieldValue}
                        setSearchFieldValue={setMarketSearchFieldValue}
                        searchFieldLength={marketSearchFieldLength}
                        setSearchFieldLength={setMarketSearchFieldLength}
                        showOptions={showMarketOptions}
                        setShowOptions={setShowMarketOptions}
                        isInFocus={isMarketInFocus}
                        setIsInFocus={setIsMarketInFocus}
                        label={marketLabel}
                        value={marketValue}
                        options={
                            isMarketRoute
                                ? filteredMarketDropdownValues
                                : marketDropdownValues
                        }
                        onFiltersChanged={onFiltersChanged}
                        resetFilters={resetAllFilters}
                    />
                ) : (
                    renderFilterStaticText(FilterDisplay.MARKET, marketLabel)
                )}
                {!isRmRoute && !isMarketRoute ? (
                    <FilterSearch
                        style={isTeamRoute ? { marginRight: '30px' } : {}}
                        filterName={FilterDisplay.TEAM}
                        filterType={FilterType.TEAM}
                        searchFieldValue={teamSearchFieldValue}
                        setSearchFieldValue={setTeamSearchFieldValue}
                        searchFieldLength={teamSearchFieldLength}
                        setSearchFieldLength={setTeamSearchFieldLength}
                        showOptions={showTeamOptions}
                        setShowOptions={setShowTeamOptions}
                        isInFocus={isTeamInFocus}
                        setIsInFocus={setIsTeamInFocus}
                        label={teamLabel}
                        value={teamValue}
                        options={
                            isTeamRoute
                                ? filteredTeamDropdownValues
                                : teamDropdownValues
                        }
                        onFiltersChanged={onFiltersChanged}
                        resetFilters={resetAllFilters}
                    />
                ) : (
                    renderFilterStaticText(FilterDisplay.TEAM, teamLabel)
                )}
                {!isTeamRoute && !isMarketRoute ? (
                    <FilterSearch
                        style={isRmRoute ? { marginRight: '30px' } : {}}
                        filterName={FilterDisplay.RM}
                        filterType={FilterType.RM}
                        searchFieldValue={rmSearchFieldValue}
                        setSearchFieldValue={setRmSearchFieldValue}
                        searchFieldLength={rmSearchFieldLength}
                        setSearchFieldLength={setRmSearchFieldLength}
                        showOptions={showRmOptions}
                        setShowOptions={setShowRmOptions}
                        isInFocus={isRmInFocus}
                        setIsInFocus={setIsRmInFocus}
                        label={rmLabel}
                        value={rmValue}
                        options={
                            isRmRoute
                                ? filteredRmDropdownValues
                                : rmDropdownValues
                        }
                        onFiltersChanged={onFiltersChanged}
                        resetFilters={resetAllFilters}
                    />
                ) : (
                    renderFilterStaticText(FilterDisplay.RM, rmLabel)
                )}
                <Timeslicer
                    monthList={tsMonthList || []}
                    onChange={onTimeSlicerChange}
                    externalReset={timeSlicerResetState}
                    start={tsStart}
                    end={tsEnd}
                />
            </div>
            <div className={styles.rightFiltersWrapper}>
                <ClearFilters onClick={resetAllFilters} />
            </div>
        </div>
    );
});

FiltersBar.displayName = 'FiltersBar';

FiltersBar.propTypes = {
    onTimeSlicerChange: PropTypes.func,
    onFiltersChanged: PropTypes.func
};

export default FiltersBar;
